import { computed, watch } from 'vue'
import env from '@/env'

import { disableRTL, enableRTL } from '@/composables/useDirection'

import i18n, { loadLocaleMessages } from '@/i18n'

import LanguageCachedDataService from '@/core/services/cachedData/LanguageCachedDataService'
import TagsCachedDataService from '@/core/services/cachedData/TagsCachedDataService'

import { Language, TagsData } from '@/types/api'

import locale from 'element-ui/lib/locale'

import en from 'element-ui/lib/locale/lang/en'
import he from 'element-ui/lib/locale/lang/he'
import ua from 'element-ui/lib/locale/lang/ua'
import { isString, objectKeysOnlyOfType } from '@/util/genericUtil'
import { DEFAULT_LANGUAGE } from '@/enum/generic'

const ELEMENT_UI_LANGUAGES = {
    en,
    he,
    ua
} as const

/* Types */
export interface LanguageMapped extends Language {
    // First element f result of split LanguageCode by '-' symbol e.g. 'en-US' => 'en'
    locale: string
    // Upper case of locale
    abbName: string
    // Full language name, same as LanguageName
    name: string
    // Path to flag icon
    icon: string
}

/* Computed */
/**
 * Gets mapped language list with additional fields like abbName, locale, name, icon
 */
export const languageList = computed<Array<LanguageMapped>>(() => {
    return LanguageCachedDataService.data.map((language) => {
        const [ locale, iconName ] = language.LanguageCode.split('-')

        return {
            ...language,
            abbName: locale.toUpperCase(),
            locale,
            name: language.LanguageName,
            icon: `/img/flags/${iconName}.png`
        }
    })
})
/**
 * Gets language by current domain
 */
export const currentLanguageByDomain = computed(() => {
    const currentDomain = window.location.hostname === 'localhost'
        ? env.VITE_APP_DEFAULT_DOMAIN_NAME
        : window.location.hostname

    return languageList.value.find(language => language.Domain === currentDomain)
})
/**
 * Gets active language by current domain or default language
 */
export const activeLanguage = computed<LanguageMapped>(() => {
    if (currentLanguageByDomain.value !== undefined) {
        return currentLanguageByDomain.value
    } else {
        return DEFAULT_LANGUAGE
    }
})

/* Methods */
function setDirection (language: LanguageMapped) {
    if (language.DomainConfig.Direction === 'rtl') {
        enableRTL()
    } else {
        disableRTL()
    }
}
function setElementUILanguage (language: LanguageMapped) {
    if (language.locale in ELEMENT_UI_LANGUAGES) {
        locale.use(ELEMENT_UI_LANGUAGES[language.locale])
    }

    locale.use(ELEMENT_UI_LANGUAGES.en)
}
function setTranslations () {
    if (!import.meta.env.PROD) {
        return
    }

    // i18n.locale = language.locale
    applyLanguageLocales(TagsCachedDataService.data)
}

function applyLanguageLocales (data: TagsData) {
    const currentData = i18n.getLocaleMessage(DEFAULT_LANGUAGE.locale)
    const localLanguages = loadLocaleMessages() as Record<string, Record<string, string>>
    const localEnValues = localLanguages['en']
    const localDefaultLanguageValues = localLanguages[DEFAULT_LANGUAGE.locale]

    const jsonData = {
        ...localEnValues,
        ...localDefaultLanguageValues
    }

    const finalData = objectKeysOnlyOfType(
        {
            ...jsonData,
            ...currentData,
            ...data
        },
        isString
    )

    i18n.mergeLocaleMessage(DEFAULT_LANGUAGE.locale, finalData)
}

/* Watchers */
watch(
    activeLanguage,
    async (value, oldValue) => {
        if (value.LanguageID === oldValue?.LanguageID) {
            return
        }

        setDirection(value)
        setElementUILanguage(value)
        setTranslations()
    },
    {
        deep: true,
        immediate: true
    }
)
watch(
    TagsCachedDataService.dataRef,
    (value) => {
        applyLanguageLocales(value)
    },
    {
        deep: true
    }
)
